import { GMXGoogleAutocompleteSearchDetail } from '../../models/google-search-address.model'
import { sanitizeInput } from '../../utils/sanitize'
import { AddressGeoDetail, getGeoDetailFromComponents } from '../../utils/postalCode'
import { whenLocationSingletonReady } from '../../utils/locationSingleton'

interface GSAGeolocationModel {
	latitude: number
	longitude: number
}

/**
 * Search - Address
 */
export class GMXGoogleSearchAddress extends HTMLElement {
	inputEl: HTMLInputElement
	searchBtn: HTMLButtonElement
	searchClearBtn: HTMLButtonElement
	autocompleteResultsEl: HTMLElement
	errorTextEl: HTMLElement

	static observedAttributes = ['data-theme']

	private _geocoder: google.maps.Geocoder

	private currentGeoLocation: GSAGeolocationModel

	private acResultsDropdownItems: HTMLElement[]

	private autocompleteSessionToken: google.maps.places.AutocompleteSessionToken

	private boundInputEventHandler = this._handleInputEvent.bind(this)
	private boundInputFocusEventHandler = this._handleInputFocus.bind(this)
	private boundInputKeydownEventHandler = this._handleInputKeydownEvent.bind(this)
	private boundClearSearchBtnClickHandler = this._handleSearchClearnBtnClick.bind(this)
	private boundSearchBtnClickHandler = this._handleSearchBtnClick.bind(this)
	private boundBodyClickEventHandler = this._handleBodyClick.bind(this)
	private boundAutocompleteResultsClickHandler = this._handleAutocompleteResultsClick.bind(this)
	private boundWindowResizeHandler = this._handleWindowResize.bind(this)

	attributeChangedCallback(name: string, oldValue: string, newValue: string) {
		if (name === 'data-theme') {
			if (this.autocompleteResultsEl) {
				this.autocompleteResultsEl.setAttribute('data-theme', newValue)
			}
		}
	}

	get labelData(): Record<string, string> {
		return this.hasAttribute('data-labels') ? JSON.parse(this.getAttribute('data-labels')) : {}
	}
	set labelData(val: Record<string, string>) {
		this.setAttribute('data-labels', JSON.stringify(val))
	}

	get isPostalCodeSearch(): boolean {
		return this.hasAttribute('data-is-postal-code-search')
	}
	set isPostalCodeSearch(val: boolean) {
		if (val) {
			this.setAttribute('data-is-postal-code-search', '')
		} else {
			this.removeAttribute('data-is-postal-code-search')
		}
	}

	get region(): string {
		// If map is used in dealer locator, get region from parent, otherwise get from self
		const dealerLocatorParent = this.closest('gmx-dealer-locator')
		return dealerLocatorParent && dealerLocatorParent.getAttribute('data-region')
			? dealerLocatorParent.getAttribute('data-region')
			: this.getAttribute('data-region')
	}
	set region(val: string) {
		this.setAttribute('data-region', val)
	}

	get language(): string {
		// If map is used in dealer locator, get language from parent, otherwise get from self
		const dealerLocatorParent = this.closest('gmx-dealer-locator')
		return dealerLocatorParent && dealerLocatorParent.getAttribute('data-language')
			? dealerLocatorParent.getAttribute('data-language')
			: this.getAttribute('data-language')
	}
	set language(val: string) {
		this.setAttribute('data-language', val)
	}

	get mapApiKey(): string {
		// If map is used in dealer locator, get map api key from parent, otherwise get from self
		const dealerLocatorParent = this.closest('gmx-dealer-locator')
		return dealerLocatorParent && dealerLocatorParent.getAttribute('data-map-api-key')
			? dealerLocatorParent.getAttribute('data-map-api-key')
			: this.getAttribute('data-map-api-key')
	}
	set mapApiKey(val: string) {
		this.setAttribute('data-map-api-key', val)
	}

	async connectedCallback(): Promise<void> {
		this.inputEl = this.querySelector('input')
		this.searchClearBtn = this.querySelector('button.search-clear-btn')
		this.searchBtn = this.querySelector('button.do-search')
		this.errorTextEl = this.querySelector('.error-text')

		if (!this.autocompleteResultsEl) {
			this.autocompleteResultsEl = document.createElement('div')
			this.autocompleteResultsEl.id = window.crypto.randomUUID()
			this.autocompleteResultsEl.classList.add('google-search-address-ac-results')
			this.autocompleteResultsEl.setAttribute('data-theme', this.getAttribute('data-theme'))

			document.body.appendChild(this.autocompleteResultsEl)

			this._positionACResultsElement()
		}

		this.autocompleteResultsEl.addEventListener('click', this.boundAutocompleteResultsClickHandler)

		this.inputEl.addEventListener('focus', this.boundInputFocusEventHandler)
		this.inputEl.addEventListener('input', this.boundInputEventHandler)
		this.inputEl.addEventListener('keydown', this.boundInputKeydownEventHandler)
		this.inputEl.setAttribute('disabled', '') // Want to make sure user can't interact until the api has loaded

		this.searchBtn.addEventListener('click', this.boundSearchBtnClickHandler)
		this.searchClearBtn.addEventListener('click', this.boundClearSearchBtnClickHandler)

		if (window.gmxGoogleMapApiLoaderSingleton?.loadStatus !== 'loaded') {
			window.addEventListener('gmxGoogleMapApiLoaded', this._setAutocomplete.bind(this), { once: true })

			if (window.gmxGoogleMapApiLoaderSingleton) {
				window.gmxGoogleMapApiLoaderSingleton.loadMapApiFromAPIKey(this.mapApiKey, this.region, this.language)
			} else {
				window.addEventListener(
					'gmxGoogleMapApiLoaderSingletonReady',
					() => {
						window.gmxGoogleMapApiLoaderSingleton.loadMapApiFromAPIKey(
							this.mapApiKey,
							this.region,
							this.language
						)
					},
					{ once: true }
				)
			}
		} else {
			this._setAutocomplete()
		}

		document.body.addEventListener('click', this.boundBodyClickEventHandler)

		window.addEventListener('resize', this.boundWindowResizeHandler)
	}

	disconnectedCallback(): void {
		this.inputEl.removeEventListener('focus', this.boundInputFocusEventHandler)
		this.inputEl.removeEventListener('input', this.boundInputEventHandler)
		this.autocompleteResultsEl.removeEventListener('click', this.boundAutocompleteResultsClickHandler)
		document.body.removeEventListener('click', this.boundBodyClickEventHandler)
		window.removeEventListener('resize', this.boundWindowResizeHandler)
	}

	/**
	 * Callback for handling window resize events
	 * @private
	 */
	private _handleWindowResize(): void {
		this._positionACResultsElement()
	}

	/**
	 * Callback for when user clicks on an item in the autocomplete results dropdown
	 * @param e {Event}
	 * @private
	 */
	private _handleAutocompleteResultsClick(e: Event): void {
		const target = e.target as HTMLElement
		const placeId = target.getAttribute('data-place-id')

		if (placeId) {
			this.hideErrorMsg()
			// this.inputEl.value = target.innerText
			this.autocompleteResultsEl.classList.remove('show')
			this._handlePlaceSelected(placeId)
		} else if (target.classList.contains('use-my-loc-row')) {
			this._handleGeolocationBtnClick()
		}
	}

	/**
	 * Method to handle when the component's inner <input> element's value changes
	 * @private
	 */
	private async _handleInputEvent(): Promise<void> {
		if (!this.autocompleteSessionToken) {
			this.autocompleteSessionToken = new google.maps.places.AutocompleteSessionToken()
		}

		if (this.inputEl.value) {
			this.inputEl.classList.add('filled')

			const request = {
				input: this.inputEl.value,
				sessionToken: this.autocompleteSessionToken,
				locationBias: this.currentGeoLocation
					? {
							north: this.currentGeoLocation.latitude + 0.5,
							south: this.currentGeoLocation.latitude - 0.5,
							east: this.currentGeoLocation.longitude + 0.5,
							west: this.currentGeoLocation.longitude - 0.5,
						}
					: undefined,
				region: this.region?.toLowerCase(),
				language: this.language,
				includedRegionCodes: [this.region],
				includedPrimaryTypes: this.isPostalCodeSearch ? ['postal_code'] : undefined,
			}

			try {
				const { suggestions } =
					await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
				if (suggestions) {
					this._positionACResultsElement()
					this._buildACResultsDropdown(suggestions)
					this.autocompleteResultsEl.classList.add('show')
				} else {
					this.autocompleteResultsEl.classList.remove('show')
					this.autocompleteResultsEl.innerHTML = ''
				}
			} catch {
				this.autocompleteResultsEl.classList.remove('show')
				this.autocompleteResultsEl.innerHTML = ''
			}
		} else {
			this.inputEl.classList.remove('filled')
			this.autocompleteResultsEl.classList.remove('show')
			this.autocompleteResultsEl.innerHTML = ''
		}
	}

	/**
	 * Callback for when the input field receives focus.  Want to automatically open/show the autocomplete results
	 * if there are any.
	 * @private
	 */
	private _handleInputFocus(): void {
		if (this.autocompleteResultsEl.querySelectorAll('.ac-pred-row').length > 0) {
			this._positionACResultsElement()
			this.autocompleteResultsEl.classList.add('show')
		}
	}

	/**
	 * Callback for click events.  Want to automatically close/hide the autocomplete results if clicked outside the
	 * element.
	 * @param e {Event}
	 * @private
	 */
	private _handleBodyClick(e: Event): void {
		if (e.target instanceof Element) {
			const target = e.target as HTMLElement

			if (!target.classList.contains('search-address')) {
				this.autocompleteResultsEl.classList.remove('show')
			}
		}
	}

	/**
	 * Callback for when component picks up a Keyboard event
	 * @param e {KeyboardEvent}
	 * @private
	 */
	private _handleInputKeydownEvent(e: KeyboardEvent): void {
		if (e.key === 'Enter' && !e.isComposing) {
			// Enter runs the address search only; never let it implicitly submit a surrounding form
			e.preventDefault()

			const currSelectedItem = this.acResultsDropdownItems?.filter(item =>
				item.classList.contains('item-selected')
			)[0]

			if (currSelectedItem?.getAttribute('data-place-id')) {
				this._handlePlaceSelected(currSelectedItem.getAttribute('data-place-id'))
			} else if (currSelectedItem?.classList.contains('use-my-loc-row')) {
				this._handleGeolocationBtnClick()
			} else {
				if (this.inputEl.checkValidity()) {
					this._dispatchSearchEvent(this.inputEl.value)
				} else {
					this.displayErrorMsg(this.inputEl.getAttribute('data-custom-invalid-error-msg'))
				}
			}

			this.autocompleteResultsEl.classList.remove('show')
		} else if (e.key === 'ArrowDown') {
			if (this.autocompleteResultsEl.classList.contains('show')) {
				const currSelectedItem = this.acResultsDropdownItems.filter(item =>
					item.classList.contains('item-selected')
				)[0]

				if (!currSelectedItem) {
					this.acResultsDropdownItems[0].classList.add('item-selected')
				} else if (currSelectedItem.classList.contains('last-item')) {
					currSelectedItem.classList.remove('item-selected')
				} else {
					this.acResultsDropdownItems.forEach((item, idx) => {
						if (item === currSelectedItem) {
							currSelectedItem.classList.remove('item-selected')
							this.acResultsDropdownItems[idx + 1].classList.add('item-selected')
							return
						}
					})
				}
			}
		} else if (e.key === 'ArrowUp') {
			if (this.autocompleteResultsEl.classList.contains('show')) {
				const currSelectedItem = this.acResultsDropdownItems.filter(item =>
					item.classList.contains('item-selected')
				)[0]

				if (!currSelectedItem) {
					this.acResultsDropdownItems[this.acResultsDropdownItems.length - 1].classList.add('item-selected')
				} else if (currSelectedItem.classList.contains('use-my-loc-row')) {
					currSelectedItem.classList.remove('item-selected')
				} else {
					this.acResultsDropdownItems.forEach((item, idx) => {
						if (item === currSelectedItem) {
							currSelectedItem.classList.remove('item-selected')
							this.acResultsDropdownItems[idx - 1].classList.add('item-selected')
							return
						}
					})
				}
			}
		}
	}

	/**
	 * Method to handle when the component's search button is clicked.
	 * @private
	 */
	private _handleSearchBtnClick(): void {
		if (this.inputEl.value) {
			if (this.inputEl.checkValidity()) {
				this._dispatchSearchEvent(this.inputEl.value)
			} else {
				this.displayErrorMsg(this.inputEl.getAttribute('data-custom-invalid-error-msg'))
			}
		}
	}

	/**
	 * Callback for when the clear input search button is clicked that signifies clearing the current search criteria
	 * from the search input element.
	 * @private
	 */
	private _handleSearchClearnBtnClick(): void {
		this.inputEl.value = ''

		// Clear any autocomplete predictions
		this.autocompleteResultsEl.innerHTML = ''
		this.acResultsDropdownItems = []
		this.inputEl.dispatchEvent(new Event('input'))
	}

	/**
	 * Bind the Google Maps Api autocomplete to the input element.
	 * @private
	 */
	private async _setAutocomplete(): Promise<void> {
		this._geocoder = new google.maps.Geocoder()

		const setAc = () => {
			this.inputEl.removeAttribute('disabled')
		}

		const success = (position: GeolocationPosition) => {
			this.currentGeoLocation = {
				latitude: position.coords.latitude,
				longitude: position.coords.longitude,
			}

			setAc()
		}
		const error = (error: GeolocationPositionError) => {
			window.console.warn(`ERROR(${error.code}): ${error.message}`)

			setAc()
		}

		const locationSingleton = await whenLocationSingletonReady()
		const akamaiZip = locationSingleton.getAkamaiLocationCookieInfo()?.zip

		if (akamaiZip) {
			// Geocode the zip so the first results are closer to where the user is and not random
			try {
				const location = await this._geocodeLocation(akamaiZip)
				this.currentGeoLocation = {
					latitude: location.geometry.location.lat(),
					longitude: location.geometry.location.lng(),
				}
			} catch (err) {
				window.console.warn(`ERROR: ${err}`)
			}

			setAc()
		} else {
			// Retrieves the current location so the first results are closer to where the user
			// is and not random
			navigator.geolocation.getCurrentPosition(success, error)
		}
	}

	/**
	 * When geolocation button is clicked, user is telling us to try to find their location and if found, trigger
	 * a search for dealers based on their current location.
	 * @private
	 */
	private async _handleGeolocationBtnClick(): Promise<void> {
		const error = (error: GeolocationPositionError) => {
			window.console.warn(`ERROR(${error.code}): ${error.message}`)

			this.displayErrorMsg(this.labelData?.geolocateErrorMsg || 'Error message missing')
		}

		const success = async (position: GeolocationPosition) => {
			const { latitude, longitude } = position.coords

			this.hideErrorMsg()

			// Reverse Geocode to get address and add to the input field
			const location = await this._geocodeLocation(new google.maps.LatLng(latitude, longitude))
			const geoDetail = getGeoDetailFromComponents(location.address_components)

			this.inputEl.dispatchEvent(new Event('input')) // Make sure to do this before setting the input value to prevent race condition with autocomplete service
			this.inputEl.value = this.isPostalCodeSearch ? geoDetail.postalCode : location.formatted_address
			this.inputEl.classList.add('filled')
			this._dispatchSearchEvent(this.inputEl.value, latitude, longitude, geoDetail)
		}

		// Retrieves the current location
		navigator.geolocation.getCurrentPosition(success, error)
	}

	/**
	 * Get location information from a location string or lat/lng location.
	 * @param location
	 * @param region
	 * @private
	 */
	private _geocodeLocation(
		location: string | google.maps.LatLng | google.maps.LatLngLiteral,
		region?: string
	): Promise<google.maps.GeocoderResult> {
		return new Promise((resolve, reject) => {
			const geocoderRequest: google.maps.GeocoderRequest =
				(location as google.maps.LatLng).lat !== undefined
					? { location: location as google.maps.LatLng }
					: {
							address: location as string,
							region: region || this.region,
						}

			this._geocoder.geocode(
				geocoderRequest,
				(result: google.maps.GeocoderResult[], status: google.maps.GeocoderStatus) => {
					if (status === google.maps.GeocoderStatus.OK) {
						const geocodedLocation = result[0] // TODO: We should return the full array and let consumers decide which is the result they want
						resolve(geocodedLocation)
					} else {
						reject(status)
					}
				}
			)
		})
	}

	/**
	 * Method to trigger a search event that can be picked up by consuming components.
	 *
	 * @param searchString {string} - Raw input element value
	 * @param lat {number} - Latitude of Location
	 * @param lng {number} - Longitude of location
	 * @param geoDetail {AddressGeoDetail} - Postal code and surrounding detail, where Google resolved one
	 * @private
	 */
	private _dispatchSearchEvent(
		searchString: string,
		lat?: number,
		lng?: number,
		geoDetail: AddressGeoDetail = {}
	): void {
		this.hideErrorMsg()

		const detail: GMXGoogleAutocompleteSearchDetail = {
			searchStr: searchString,
			location:
				lat && lng
					? {
							lat: lat,
							lng: lng,
						}
					: undefined,
			...geoDetail,
		}

		this.dispatchEvent(
			new CustomEvent<GMXGoogleAutocompleteSearchDetail>('gmxGoogleAutocompleteSearch', { detail })
		)
	}

	/**
	 * Method to position the autocomplete results element below the search field
	 * @private
	 */
	private _positionACResultsElement(): void {
		const boundingClientRect = this.getBoundingClientRect()

		this.autocompleteResultsEl.style.left = `${boundingClientRect.left}px`
		this.autocompleteResultsEl.style.top = `${window.scrollY + boundingClientRect.top + this.inputEl.offsetHeight}px`
		this.autocompleteResultsEl.style.width = `${this.offsetWidth}px`
	}

	/**
	 * From a list of autocomplete predictions from the Google Places service, build the autocomplete dropdown
	 * @param predictions
	 * @private
	 */
	private _buildACResultsDropdown(suggestions: google.maps.places.AutocompleteSuggestion[]): void {
		this.autocompleteResultsEl.innerHTML = ''
		this.acResultsDropdownItems = []

		const newUseMyLocRowEl = document.createElement('div') as HTMLElement
		newUseMyLocRowEl.classList.add('use-my-loc-row')
		newUseMyLocRowEl.innerText = this.labelData?.useLocation || 'Use My Location'
		this.autocompleteResultsEl.appendChild(newUseMyLocRowEl)
		this.acResultsDropdownItems.push(newUseMyLocRowEl)

		suggestions.forEach((sgstn, idx) => {
			const placePrediction = sgstn.placePrediction

			const newEl = document.createElement('div') as HTMLElement
			newEl.classList.add('ac-pred-row')
			newEl.setAttribute('data-place-id', sanitizeInput(placePrediction.placeId))

			if (idx === suggestions.length - 1) {
				// Is las
				newEl.classList.add('last-item')
			}

			newEl.textContent = placePrediction.text.text

			this.autocompleteResultsEl.appendChild(newEl)
			this.acResultsDropdownItems.push(newEl)
		})
	}

	/**
	 * Callback for when a place is selected from the autocomplete dropdown.
	 * @param placeId {string}
	 * @private
	 */
	private async _handlePlaceSelected(placeId: string): Promise<void> {
		this.autocompleteSessionToken = undefined

		const place = new google.maps.places.Place({ id: placeId })
		await place.fetchFields({
			fields: ['displayName', 'formattedAddress', 'location', 'addressComponents'],
		})

		this.hideErrorMsg()

		if (this.isPostalCodeSearch) {
			this.inputEl.value = place.displayName
		} else {
			this.inputEl.value = place.formattedAddress
		}

		this._dispatchSearchEvent(
			this.inputEl.value,
			place?.location?.lat(),
			place?.location?.lng(),
			getGeoDetailFromComponents(place.addressComponents)
		)
	}

	displayErrorMsg(errorMsg: string): void {
		this.classList.add('invalid')
		this.errorTextEl.innerText = errorMsg
	}

	hideErrorMsg(): void {
		this.classList.remove('invalid')
	}

	/**
	 * Clear the search input, autocomplete results, and error state without triggering a new search.
	 */
	reset(): void {
		if (this.inputEl) {
			this.inputEl.value = ''
			this.inputEl.classList.remove('filled')
		}

		if (this.autocompleteResultsEl) {
			this.autocompleteResultsEl.innerHTML = ''
			this.autocompleteResultsEl.classList.remove('show')
		}

		this.acResultsDropdownItems = []
		this.hideErrorMsg()
	}
}

if (!customElements.get('gmx-google-search-address')) {
	customElements.define('gmx-google-search-address', GMXGoogleSearchAddress)
}
